import { chunk, deduplicateById, parseReference } from "./fhir.utils";
import { FhirClientPort, SlotResource } from "./fhir.types";

export interface SlotModel {
  id: string;
  scheduleId: string;
  scheduleReference: string;
  status: string;
  start: string;
  end: string;
  overbooked: boolean;
  versionId?: string;
}

function mapSlot(resource: SlotResource): SlotModel | undefined {
  const schedule = parseReference(resource.schedule?.reference);
  if (!resource.id || !schedule || !resource.start || !resource.end) {
    return undefined;
  }

  return {
    id: resource.id,
    scheduleId: schedule.id,
    scheduleReference: schedule.key,
    status: resource.status ?? "unknown",
    start: resource.start,
    end: resource.end,
    overbooked: resource.overbooked === true,
    versionId: resource.meta?.versionId,
  };
}

export class SlotService {
  constructor(private readonly client: FhirClientPort) {}

  async searchBySchedules(
    scheduleIds: string[],
    rangeStart: string,
    rangeEnd: string,
  ): Promise<SlotModel[]> {
    if (scheduleIds.length === 0) {
      return [];
    }

    const bundles = await Promise.all(
      chunk(scheduleIds, 20).map((ids) =>
        this.client.search("Slot", {
          schedule: ids.map((id) => `Schedule/${id}`).join(","),
          start: [`ge${rangeStart}`, `lt${rangeEnd}`],
          _count: 500,
        }),
      ),
    );

    const slots = bundles.flatMap((bundle) =>
      (bundle.entry ?? [])
        .map((entry) => entry.resource)
        .filter(
          (resource): resource is SlotResource =>
            resource?.resourceType === "Slot",
        )
        .map(mapSlot)
        .filter((slot): slot is SlotModel => Boolean(slot)),
    );

    return deduplicateById(slots);
  }

  async readResources(slotIds: string[]): Promise<SlotResource[]> {
    const resources = await Promise.all(
      Array.from(new Set(slotIds)).map((id) => this.client.read("Slot", id)),
    );

    return resources.filter(
      (resource): resource is SlotResource => resource.resourceType === "Slot",
    );
  }
}
